/**
 * Footer Component
 * Site footer with navigation, newsletter, and trust badges
 */

import { useState, useEffect } from 'react';
import { subscribeToNewsletter, getShopMetafields, parseMetafields, getMetafieldValue } from '~/lib/shopify/api';
import {
  IconShippingFast,
  IconReturn,
  IconSupport,
  IconShieldCheck,
  IconLinkedIn,
  IconTwitter,
  IconFacebook,
  IconYouTube,
  IconArrowRight,
  IconCheck,
} from '../ui/Icons';

const footerNav = {
  shop: {
    title: 'Shop',
    links: [
      { label: 'All Headsets', href: '/products' },
      { label: 'Jabra', href: '/collections/jabra' },
      { label: 'Poly', href: '/collections/poly' },
      { label: 'Axtel', href: '/collections/axtel' },
      { label: 'Refurbished', href: '/collections/refurbished' },
      { label: 'Best Sellers', href: '/products' },
    ],
  },
  support: {
    title: 'Support',
    links: [
      { label: 'Help Centre', href: '/support' },
      { label: 'Shipping Info', href: '/shipping' },
      { label: 'Compatibility Guide', href: '/compatibility' },
      { label: 'Setup Instructions', href: '/setup' },
      { label: 'Warranty Info', href: '/warranty' },
      { label: 'Returns & Refunds', href: '/returns' },
      { label: 'Contact Us', href: '/contact' },
    ],
  },
  company: {
    title: 'Company',
    links: [
      { label: 'About Us', href: '/about' },
      { label: 'Blog', href: '/blog' },
      { label: 'Careers', href: '/careers' },
      { label: 'Partner Program', href: '/partners' },
    ],
  },
  legal: {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms & Conditions', href: '/terms' },
      { label: 'Cookie Policy', href: '/cookies' },
    ],
  },
};


const trustBadges = [
  {
    icon: IconShippingFast,
    label: 'Free Next-Day Delivery',
    sublabel: 'Orders over £75',
  },
  {
    icon: IconReturn,
    label: '30-Day Returns',
    sublabel: 'Hassle-free',
  },
  {
    icon: IconSupport,
    label: 'Advice',
    sublabel: 'Real specialists',
  },
  {
    icon: IconShieldCheck,
    label: 'Genuine Brands',
    sublabel: 'Jabra, Poly, Axtel',
  },
];

const socialIconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  linkedin: IconLinkedIn,
  twitter: IconTwitter,
  facebook: IconFacebook,
  youtube: IconYouTube,
};

export function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [socialLinks, setSocialLinks] = useState<Array<{ label: string; href: string; icon: React.ComponentType<{ size?: number; className?: string }> }>>([]);

  useEffect(() => {
    getShopMetafields().then((shop) => {
      if (!shop?.metafields) return;
      const parsed = parseMetafields(shop.metafields);
      const links = getMetafieldValue<Record<string, string>>(parsed, 'social_links', {});
      const resolved = Object.entries(links)
        .filter(([key, url]) => url && socialIconMap[key])
        .map(([key, url]) => ({
          label: key.charAt(0).toUpperCase() + key.slice(1),
          href: url,
          icon: socialIconMap[key],
        }));
      setSocialLinks(resolved);
    });
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await subscribeToNewsletter(email);
      if (result.success) {
        setIsSubscribed(true);
        setEmail('');
      } else {
        setError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-surface-coal border-t border-surface-slate/50">
      {/* Trust Badges */}
      <div className="border-b border-surface-slate/30">
        <div className="container-wide py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {trustBadges.map((badge) => (
              <div key={badge.label} className="flex items-center gap-4">
                <div className="w-12 h-12 bg-surface-graphite rounded-xl flex items-center justify-center flex-shrink-0 border border-surface-slate/30">
                  <badge.icon size={22} className="text-brand-orange" />
                </div>
                <div>
                  <div className="font-medium text-accent-white text-sm">{badge.label}</div>
                  <div className="text-xs text-accent-silver">{badge.sublabel}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-wide py-12 lg:py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Logo & Newsletter */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <a href="/" className="inline-flex items-center mb-6 group">
              <img
                src="https://cdn.shopify.com/s/files/1/0952/3678/4460/files/itr-logo.png?v=1770583066"
                alt="IT Remarketing"
                className="h-12 w-auto brightness-0 invert transition-transform duration-300 group-hover:scale-105"
              />
            </a>

            <p className="text-sm text-accent-silver mb-6 max-w-sm leading-relaxed">
              Your source for professional communication headsets. Next-day delivery and 30-day returns on all orders.
            </p>

            {/* Newsletter */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-accent-white mb-3">Stay Updated</h3>
              {isSubscribed ? (
                <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                  <IconCheck size={18} className="text-emerald-400" />
                  <span className="text-sm text-emerald-400">Thanks for subscribing!</span>
                </div>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="input flex-1 py-2.5 text-sm"
                      disabled={isSubmitting}
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary px-4 py-2.5 text-xs disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <IconArrowRight size={16} />
                      )}
                    </button>
                  </div>
                  {error && (
                    <p className="text-xs text-red-400">{error}</p>
                  )}
                </form>
              )}
              <p className="text-[11px] text-accent-silver/70 mt-2">
                Get exclusive offers and product updates. Unsubscribe anytime.
              </p>
            </div>

            {/* Social Links - only shown if configured in Shopify */}
            {socialLinks.length > 0 && (
              <div className="flex gap-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-surface-graphite rounded-xl flex items-center justify-center text-accent-silver hover:text-accent-white hover:bg-surface-slate border border-surface-slate/30 hover:border-surface-slate transition-all duration-200"
                    aria-label={social.label}
                  >
                    <social.icon size={18} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Navigation Columns */}
          {Object.values(footerNav).map((section) => (
            <div key={section.title}>
              <h3 className="text-[11px] font-mono uppercase tracking-[0.15em] text-accent-silver/70 mb-4">
                {section.title}
              </h3>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className="text-sm text-accent-platinum hover:text-brand-orange transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Business Information - Required for Google Merchant Center */}
      <div className="border-t border-surface-slate/30 bg-surface-graphite/50">
        <div className="container-wide py-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 text-xs text-accent-silver">
            <div>
              <h4 className="font-medium text-accent-platinum mb-2">Registered Office</h4>
              <address className="not-italic">
                IT Remarketing Limited<br />
                Swan Ken, Broadbridge Heath Road<br />
                Warnham, Horsham, RH12 3PA
              </address>
            </div>
            <div>
              <h4 className="font-medium text-accent-platinum mb-2">Contact</h4>
              <p>
                Phone: <a href="tel:+442045915420" className="hover:text-brand-orange">020 4591 5420</a><br />
                Email: <a href="mailto:sales@itremarketing.co.uk" className="hover:text-brand-orange">sales@itremarketing.co.uk</a><br />
                Mon–Fri, 9am–5pm
              </p>
            </div>
            <div>
              <h4 className="font-medium text-accent-platinum mb-2">Company Information</h4>
              <p>
                Company No: 15820855<br />
                VAT No: GB477755043<br />
                Registered in England & Wales
              </p>
            </div>
            <div>
              <h4 className="font-medium text-accent-platinum mb-2">Secure Shopping</h4>
              <p>
                256-bit SSL Encryption<br />
                PCI DSS Compliant<br />
                Safe & Secure Checkout
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-surface-slate/30">
        <div className="container-wide py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-accent-silver">
              © {new Date().getFullYear()} IT Remarketing Limited. All rights reserved.
            </div>

            {/* Payment Methods */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-accent-silver/70 mr-2">Secure payments</span>
              <div className="flex gap-1.5">
                {['Visa', 'MC', 'Amex', 'PayPal', 'Apple'].map((method) => (
                  <div
                    key={method}
                    className="w-10 h-6 bg-surface-graphite rounded flex items-center justify-center border border-surface-slate/30"
                  >
                    <span className="text-[10px] text-accent-silver font-mono">{method}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
